import * as React from 'react';
import { Campaign, Project, ProjectFactory } from '../../resource/';
import { IAuthProps, Query } from '../../services/';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Section } from '../../modules';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { MarkdownTextarea } from '@independent-software/typeui/modules/MarkdownTextarea';
import { Label } from '@independent-software/typeui/controls/Label';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { TidalSituation } from './TidalSituation';

interface IProps {
  /** Initial form data. */
  data: Campaign;
  /** Called whenever form changes. */
  onChange: (data: Campaign, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

interface IState {
  /** Current form data. */
  data: Campaign;
  /** Data for projects dropdown */
  projects: Project[];  
}

class CampaignForm extends React.Component<IAuthProps & IProps, IState> {
  constructor(props: IAuthProps & IProps) {
    super(props);

    // Intialize state.
    this.state = {
      data: props.data,
      projects: []
    };    
  }

  componentDidMount = () => {
    this.handleProjectSearch();
  }

  handleProjectSearch = (q?: string) => {
    // Retrieve a list of projects:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ProjectFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ projects: res.items }));
  }

  renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={this.state.data.name}
      control={(<Input type="text" placeholder="Name" fluid/>)}
      required={{message: "A campaign name is required."}}
      minLength={{length: 3, message: "Campaign name must be at least 3 characters."}}
      maxLength={{length: 255, message: "Campaign name may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a campaign name.</React.Fragment>)}/>

  renderDate = () =>
    <Form.Field
      name="date" label="Date"
      value={this.state.data.date}
      control={(<Input type="date" nofuture placeholder="Date" fluid/>)}
      hint={(<React.Fragment>Please select a campaign date.</React.Fragment>)}/>

  renderProject = () => 
    <Form.Field
      width={1} label="Project"
      name="project" 
      value={this.state.data.project}
      control={(<Dropdown clearable onSearch={this.handleProjectSearch} fluid data={this.state.projects} placeholder="Project" label={(item:Project) => item.name}>
        <Dropdown.Column weight={1}>{(item:Project) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a project."/>  

  renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={this.state.data.description}
      control={(<MarkdownTextarea rows={16} fluid/>)}
      hint={(<React.Fragment>Please enter a campaign description.</React.Fragment>)}/>    
            
  renderFreshwaterDischarge = () =>
    <Form.Field 
      name="freshwater_discharge" label="Freshwater discharge"
      value={this.state.data.freshwater_discharge}
      control={(<Input type="text" placeholder="Freshwater discharge" fluid><Label attached="right" color="#aaa" pointing>m<sup>3</sup>/s</Label></Input>)}
      isFloat={{message: "Freshwater discharge must be a numeric value."}}
      min={{value: 0, message: "Freshwater discharge must be at least 0."}}
      hint={(<React.Fragment>Please enter the river flow into the system.</React.Fragment>)}/>    

  renderTidalAmplitude = () =>
    <Form.Field 
      name="tidal_amplitude" label="Tidal amplitude"
      value={this.state.data.tidal_amplitude}
      control={(<Input type="text" placeholder="Tidal amplitude" fluid><Label attached="right" color="#aaa" pointing>m</Label></Input>)}
      isFloat={{message: "Tidal amplitude must be a numeric value."}}
      hint={(<React.Fragment>Please enter the maximum water height above mid-tide mark.</React.Fragment>)}/>    
      
  renderTidalSituation = () =>
    <Form.Field 
      name="tidal_situation" label="Tidal situtation"
      value={this.state.data.tidal_situation}
      control={<Dropdown placeholder="Tidal situation" fluid data={TidalSituation.getList()}
        label={(item) => TidalSituation.toName(item)}>
        <Dropdown.Column>{(item) => TidalSituation.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the tide condition.</React.Fragment>)}/>         

  renderHighTime = () =>
    <Form.Field 
      name="high_time" label="High water time"
      value={this.state.data.high_time}
      control={(<Input type="time" placeholder="High water time" fluid is24h clock format="HH:mm"/>)}
      hint={(<React.Fragment>Please select a high water time.</React.Fragment>)}/>          
        
  renderLowTime = () =>
    <Form.Field 
      name="low_time" label="Low water time"
      value={this.state.data.low_time}
      control={(<Input type="time" placeholder="Low water time" fluid is24h clock format="HH:mm"/>)}
      hint={(<React.Fragment>Please select a low water time.</React.Fragment>)}/>    

  renderRadiation = () =>
    <Form.Field 
      name="radiation" label="Solar radiation"
      value={this.state.data.radiation}
      control={(<Input type="text" placeholder="Solar radiation" fluid><Label attached="right" color="#aaa" pointing>W/m<sup>2</sup></Label></Input>)}
      isFloat={{message: "Solar radiation must be a numeric value."}}
      min={{value: 0, message: "Solar radiation must be at least 0."}}
      hint={(<React.Fragment>Please enter the maximum solar radiation.</React.Fragment>)}/>          
  
  renderRainfall = () =>
    <Form.Field 
      name="rainfall" label="Rainfall"
      value={this.state.data.rainfall}
      control={(<Input type="text" placeholder="Rainfall" fluid><Label attached="right" color="#aaa" pointing>mm</Label></Input>)}
      isFloat={{message: "Rainfall must be a numeric value."}}
      min={{value: 0, message: "Cumulative rainfall must be at least 0."}}
      hint={(<React.Fragment>Please enter the cumulative rainfall for the previous five days.</React.Fragment>)}/>

  renderLowTemperature = () =>
    <Form.Field 
      name="low_temperature" label="Air temperature (min)"
      value={this.state.data.low_temperature}
      control={(<Input type="text" placeholder="Air temperature (min)" fluid><Label attached="right" color="#aaa" pointing>&ordm;C</Label></Input>)}
      isFloat={{message: "Minimum air temperature must be a numeric value."}}
      hint={(<React.Fragment>Please enter the lowest registered air temperature.</React.Fragment>)}/>

  renderHighTemperature = () =>
    <Form.Field 
      name="high_temperature" label="Air temperature (max)"
      value={this.state.data.high_temperature}
      control={(<Input type="text" placeholder="Air temperature (max)" fluid><Label attached="right" color="#aaa" pointing>&ordm;C</Label></Input>)}
      isFloat={{message: "Maximum air temperature must be a numeric value."}}
      hint={(<React.Fragment>Please enter the highest registered air temperature.</React.Fragment>)}/>

  renderWindDirection = () =>
    <Form.Field 
      name="wind_direction" label="Wind direction"
      value={this.state.data.wind_direction}
      control={(<Input type="text" placeholder="Wind direction" fluid><Label attached="right" color="#aaa" pointing>deg</Label></Input>)}
      isInt={{message: "Wind direction must be a whole number."}}
      max={{value: 360, message: "Wind direction must be between 0 and 360 degrees."}}
      min={{value: 0, message: "Wind direction must be between 0 and 360 degrees."}}
      hint={(<React.Fragment>Please enter the wind direction in degrees (north is 360).</React.Fragment>)}/>

  renderWindSpeed = () =>
    <Form.Field 
      name="wind_speed" label="Wind speed"
      value={this.state.data.wind_speed}
      control={(<Input type="text" placeholder="Wind speed" fluid><Label attached="right" color="#aaa" pointing>m/s</Label></Input>)}
      isFloat={{message: "Wind speed must be a numeric value."}}
      min={{value: 0, message: "Wind direction must be at least 0."}}
      hint={(<React.Fragment>Please enter the wind speed in meters per second.</React.Fragment>)}/>
      
  renderWeather = () =>
    <Form.Field 
      name="weather" label="Weather"
      value={this.state.data.weather}
      control={(<Input type="text" placeholder="Weather" fluid></Input>)}
      maxLength={{length: 255, message: "Weather may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter the weather conditions (e.g. slightly overcast,warm, light breeze).</React.Fragment>)}/>         

  render() {
    let p = this.props;
    return (
      <Form
        data={this.state.data} dirty={p.dirty} onChange={p.onChange} onValidate={p.onValidate}>
        <Tabs underlined>
          <Tabs.Pane label="General">
            <Section padded>
              <Flex.Columns count={2}>
                <>
                  {this.renderName()}
                  {this.renderDate()}
                  {this.renderProject()}
                </>
                <>
                  {this.renderDescription()}
                </>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
          <Tabs.Pane label="Hydrology">
            <Section padded>
              <Flex.Columns count={2}>
                <>{this.renderFreshwaterDischarge()}</>
                <></>
                <>{this.renderTidalAmplitude()}</>
                <>{this.renderTidalSituation()}</>
                <>{this.renderHighTime()}</>
                <>{this.renderLowTime()}</>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
          <Tabs.Pane label="Weather">
            <Section padded>
              <Flex.Columns count={2}>
                <>{this.renderRadiation()}</>
                <>{this.renderRainfall()}</>
                <>{this.renderLowTemperature()}</>
                <>{this.renderHighTemperature()}</>
                <>{this.renderWindDirection()}</>
                <>{this.renderWindSpeed()}</>
                <>{this.renderWeather()}</>
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
        </Tabs>
      </Form>
    )
  }
}

export { CampaignForm };
